import React, { useState } from 'react';
import axios from 'axios';
import { 
  Users, 
  BarChart3, 
  Zap, 
  ArrowRight, 
  Clock, 
  FileStack, 
  Laptop,
  CheckCircle,
  Calendar,
  Menu,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hand-drawn Arrow SVG Component
const HandDrawnArrow = ({ className = "" }) => (
  <svg className={`inline-block ${className}`} width="40" height="20" viewBox="0 0 40 20" fill="none">
    <path 
      d="M2 10C2 10 8 8 15 10C22 12 28 8 38 10M38 10L32 5M38 10L32 15" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      style={{ strokeDasharray: "none" }}
    />
  </svg>
);

// Hand-drawn Asterisk Component
const HandDrawnAsterisk = ({ className = "" }) => (
  <svg className={`inline-block ${className}`} width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path 
      d="M12 2V22M2 12H22M4 4L20 20M20 4L4 20" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
  </svg>
);

// Hand-drawn Circle Component
const HandDrawnCircle = ({ children, className = "" }) => (
  <span className={`relative inline-block ${className}`}>
    {children}
    <svg className="absolute -inset-2 w-[calc(100%+16px)] h-[calc(100%+16px)]" viewBox="0 0 100 40" fill="none" preserveAspectRatio="none">
      <ellipse 
        cx="50" 
        cy="20" 
        rx="48" 
        ry="18" 
        stroke="currentColor" 
        strokeWidth="2" 
        fill="none"
        style={{ transform: "rotate(-2deg)", transformOrigin: "center" }}
      />
    </svg>
  </span>
);

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navLinks = [
    { name: 'Inicio', href: '#inicio' },
    { name: 'Nosotras', href: '#nosotras' },
    { name: 'Servicios', href: '#servicios' },
    { name: 'Metodologia', href: '#metodologia' },
    { name: 'Contacto', href: '#contacto' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-cream border-b-2 border-text-dark" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <a href="#inicio" className="flex items-center" data-testid="logo-link">
            <span className="font-poppins font-black text-2xl md:text-3xl tracking-tight text-text-dark">
              BOOST<span className="text-pink">CHANGE</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link font-poppins font-semibold text-text-dark hover:text-pink transition-colors"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contacto"
              className="bg-pink text-white font-poppins font-bold uppercase tracking-widest px-6 py-3 border-2 border-text-dark shadow-hard hover:translate-y-1 hover:shadow-none transition-all"
              data-testid="nav-cta-button"
            >
              Hablemos
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t-2 border-text-dark" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 font-poppins font-semibold text-text-dark hover:text-pink"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contacto"
              className="inline-block mt-4 bg-pink text-white font-poppins font-bold uppercase tracking-widest px-6 py-3 border-2 border-text-dark shadow-hard"
              onClick={() => setMobileMenuOpen(false)}
            >
              Hablemos
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section id="inicio" className="bg-cream min-h-[90vh] flex items-center" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-stretch">
        {/* Text Content - 50% */}
        <div className="flex flex-col justify-center space-y-8">
          <h1 className="font-poppins font-black text-giant text-text-dark leading-[0.95]" data-testid="hero-title">
            Multiplica el impacto de tu organización{' '}
            <span className="text-pink">sin agotar</span> a tu equipo
          </h1>
          
          <p className="font-poppins text-xl md:text-2xl text-text-dark/80 max-w-xl leading-relaxed">
            Acompañamos a fundaciones y organizaciones sociales a profesionalizar su gestión 
            con herramientas prácticas y humanas.
          </p>

          <div className="flex flex-wrap items-center gap-6 pt-4">
            <a
              href="#contacto"
              className="group inline-flex items-center gap-3 bg-pink text-white font-poppins font-bold uppercase tracking-widest px-8 py-4 border-2 border-text-dark shadow-hard-lg hover:translate-y-1 hover:shadow-hard transition-all"
              data-testid="hero-cta-button"
            >
              SUMATE AHORA
              <HandDrawnArrow className="hand-arrow" />
            </a>
          </div>
        </div>

        {/* Visual - 50% with massive image */}
        <div className="relative flex items-center">
          <div 
            className="relative bg-cyan border-[3px] border-text-dark shadow-hard-lg w-full"
            style={{ 
              boxShadow: '8px 8px 0px 0px rgba(0,0,0,1)',
              transform: 'rotate(-1deg)'
            }}
          >
            <div className="aspect-[4/3] w-full overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/nbyv8en0_WhatsApp%20Image%202026-03-08%20at%2011.36.45%20PM.jpeg"
                alt="Comunidad Boost Change"
                className="w-full h-full border-0"
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Problem Section (Yellow Background)
const ProblemSection = () => {
  const challenges = [
    { 
      title: "Operatividad que asfixia",
      description: "Pierdes mucho tiempo en tareas manuales y procesos lentos. Esto te quita tiempo y energía para pensar en la estrategia y el impacto."
    },
    { 
      title: "Cambio sin evidencia",
      description: "Avanzas a ciegas. Faltan sistemas para medir el cambio real que logras y tiempo para adoptar herramientas digitales que te ayuden en el día a día."
    },
    { 
      title: "El desgaste de innovar solos",
      description: "Intentar impulsar a tu equipo y transformar tu organización sin herramientas, sin formación práctica y sin una red de pares que entienda tus retos, es agotador."
    },
  ];

  return (
    <section id="retos" className="bg-yellow py-20 md:py-32" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-poppins font-black text-4xl md:text-5xl lg:text-6xl text-text-dark uppercase tracking-tight" data-testid="problem-title">
            SABEMOS QUÉ TE ESTÁ FRENANDO
          </h2>
          
          <p className="mt-6 font-poppins text-lg md:text-xl text-text-dark max-w-3xl mx-auto leading-relaxed">
            Impulsar el cambio social exige más que pasión; requiere sistemas que funcionen. 
            Estos son los tres retos que te frenan a ti y a tu equipo y que nosotros podemos apoyarte a resolverlos.
          </p>
          
          <div className="mt-8 inline-block bg-cream border-[3px] border-text-dark px-6 py-3" style={{ boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)' }}>
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">RETOS COMUNES</span>
          </div>
        </div>

        {/* Challenge Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {challenges.map((challenge, index) => (
            <div
              key={index}
              className="bg-cream border-[3px] border-text-dark p-8 relative"
              style={{ boxShadow: '6px 6px 0px 0px rgba(0,0,0,1)' }}
              data-testid={`villain-card-${index}`}
            >
              {/* Number badge - Neo-brutalista */}
              <div 
                className="absolute -top-4 -left-4 w-12 h-12 bg-pink border-[3px] border-text-dark flex items-center justify-center"
                style={{ boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)' }}
              >
                <span className="font-poppins font-black text-xl text-white">{index + 1}</span>
              </div>
              
              <div className="pt-4">
                <h3 className="font-poppins font-bold text-xl md:text-2xl text-text-dark mb-4">{challenge.title}</h3>
                <p className="font-poppins text-text-dark/80 leading-relaxed">{challenge.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Us Section - Quiénes Somos
const AboutSection = () => (
  <section id="nosotras" className="bg-cream py-20 md:py-32 overflow-hidden" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-stretch">
        {/* Text Content */}
        <div className="flex flex-col justify-center space-y-8">
          <h2 className="font-poppins font-black text-4xl md:text-5xl lg:text-6xl leading-[1.1]" data-testid="about-title">
            <span className="text-text-dark">HOLA, SOMOS</span>{' '}
            <span className="text-pink">BOOST CHANGE</span>
          </h2>
          
          <p className="font-poppins text-lg md:text-xl leading-relaxed" style={{ color: '#222222' }}>
            Somos personas del sector social diseñando para el sector social. Nacimos en Ecuador 
            para crear exactamente las herramientas que a nosotros mismos nos hacían falta en el terreno. 
            Hoy impulsamos a organizaciones en toda América Latina.
          </p>
          
          <p className="font-poppins text-lg md:text-xl leading-relaxed" style={{ color: '#222222' }}>
            No somos la típica consultora corporativa. Somos una{' '}
            <strong className="text-pink">empresa social</strong> que te acompaña a lograr 
            procesos ágiles y a escalar tus resultados, manteniéndonos{' '}
            <strong className="text-pink">siempre humanos</strong>. Medimos nuestro éxito 
            de tres formas: el estrés que te quitamos de encima, las nuevas capacidades 
            que adquiere tu equipo y el cambio que impulsan en el terreno.
          </p>

          {/* Badges - Aligned left with text */}
          <div className="flex flex-wrap gap-4 pt-4">
            {[
              { text: "IMPACTO REAL", color: "bg-lime" },
              { text: "AGILIDAD", color: "bg-cyan" },
              { text: "HUMANIDAD", color: "bg-pink" }
            ].map((badge, index) => (
              <div 
                key={index}
                className={`${badge.color} px-5 py-2 border-[3px] border-text-dark`}
                style={{ boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)' }}
                data-testid={`about-badge-${index}`}
              >
                <span className="font-poppins font-bold text-sm md:text-base text-text-dark uppercase tracking-wide">
                  {badge.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Visual - Neo-Brutalist Photo Frame */}
        <div className="flex items-center justify-center">
          <div 
            className="relative bg-white w-full max-w-lg"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px 0px rgba(0,0,0,1)',
              transform: 'rotate(1deg)'
            }}
            data-testid="about-polaroid"
          >
            <div className="aspect-[4/3] w-full overflow-hidden">
              <img
              src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/8x0qd5pi_IMG_8642.jpeg"
              alt="Equipo Boost Change"
              className="w-full h-full"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
              data-testid="about-team-photo"
            />
            </div>
            {/* Caption bar */}
            <div className="bg-white py-4 text-center border-t-4 border-text-dark">
              <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Ecuador → Latinoamérica</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section (What We Do)
const ServicesSection = () => {
  const services = [
    {
      icon: <Users className="w-12 h-12" />,
      name: "Impact Boost Academy",
      color: "pink",
      bgColor: "bg-pink",
      description: "Formacion que no se queda en el papel. Cursos practicos para equipos de impacto.",
      features: ["Plataforma educativa", "Certificados digitales", "Comunidad virtual"],
      link: "/impact-boost-academy"
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      name: "Sistemas de Impacto",
      color: "purple",
      bgColor: "bg-purple",
      description: "Dashboards vivos, no reportes muertos. Mide lo que importa de forma simple.",
      features: ["Frameworks MEL", "Evaluacion participativa", "Planeacion estrategica"],
      link: "/sistemas-de-impacto"
    },
    {
      icon: <Zap className="w-12 h-12" />,
      name: "Boost Ops",
      color: "lime",
      bgColor: "bg-lime",
      description: "Tecnologia que trabaja para ti. Automatizacion y herramientas agiles.",
      features: ["Automatizacion de flujos", "Herramientas digitales", "Tech for Good"],
      link: "/boost-ops"
    },
  ];

  return (
    <section id="servicios" className="bg-cream py-20 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-poppins font-black text-4xl md:text-6xl text-text-dark" data-testid="services-title">
            QUE HACEMOS?
          </h2>
          <p className="mt-4 font-poppins font-medium text-lg text-text-dark/70">
            Tres pilares para impulsar tu impacto
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card bg-white border-2 border-text-dark shadow-hard-lg p-8 relative"
              data-testid={`service-card-${index}`}
            >
              {/* Color indicator */}
              <div className={`absolute top-0 left-0 w-full h-2 ${service.bgColor}`}></div>
              
              {/* Icon */}
              <div className={`inline-block p-4 ${service.bgColor} border-2 border-text-dark shadow-hard mb-6`}>
                <div className="text-white">{service.icon}</div>
              </div>

              <h3 className="font-poppins font-bold text-2xl text-text-dark mb-3">{service.name}</h3>
              <p className="font-poppins text-text-dark/70 mb-6">{service.description}</p>

              {/* Features */}
              <ul className="space-y-2">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 font-poppins text-sm text-text-dark">
                    <span className={`w-2 h-2 ${service.bgColor} rounded-full`}></span>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA - Link to subpage */}
              <a
                href={service.link}
                className="mt-6 inline-flex items-center gap-2 font-poppins font-bold text-text-dark hover:text-pink transition-colors"
                data-testid={`service-link-${index}`}
              >
                Conoce mas <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Methodology Section (Blue Background)
const MethodologySection = () => {
  const steps = [
    { number: "01", name: "Reconocer", description: "Entendemos tu realidad y desafios" },
    { number: "02", name: "Fortalecer", description: "Desarrollamos capacidades clave" },
    { number: "03", name: "Formalizar", description: "Sistematizamos procesos" },
    { number: "04", name: "Conectar", description: "Integramos todo en ecosistema" },
  ];

  return (
    <section id="metodologia" className="bg-blue py-20 md:py-32" data-testid="methodology-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-poppins font-black text-4xl md:text-6xl text-white" data-testid="methodology-title">
            UN CAMINO PROBADO
          </h2>
          <p className="mt-4 font-poppins font-medium text-lg text-white/80">
            Nuestra metodologia en 4 pasos
          </p>
        </div>

        {/* Path Visualization */}
        <div className="relative">
          {/* Dashed Path SVG */}
          <svg className="hidden md:block absolute top-1/2 left-0 w-full h-4 -translate-y-1/2" preserveAspectRatio="none">
            <path 
              d="M0,8 Q200,0 400,8 T800,8 T1200,8" 
              stroke="white" 
              strokeWidth="3" 
              strokeDasharray="10,10" 
              fill="none"
              strokeLinecap="round"
            />
          </svg>

          {/* Steps */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 relative z-10">
            {steps.map((step, index) => (
              <div
                key={index}
                className="methodology-step bg-cream border-2 border-text-dark shadow-hard p-6 text-center"
                data-testid={`methodology-step-${index}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="inline-block bg-yellow border-2 border-text-dark px-3 py-1 mb-4 rotate-organic">
                  <span className="font-poppins font-black text-lg text-text-dark">{step.number}</span>
                </div>
                <h3 className="font-poppins font-bold text-xl text-text-dark mb-2">{step.name}</h3>
                <p className="font-poppins text-sm text-text-dark/70">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Promise/Climax Section - Neo-Brutalist Clean Design
const PromiseSection = () => (
  <section className="bg-cream py-20 md:py-32" data-testid="promise-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Text Content - Left aligned */}
        <div className="space-y-8 order-2 md:order-1">
          {/* Label - No rotation, clean rectangle */}
          <div 
            className="inline-block bg-cyan px-5 py-2"
            style={{ 
              border: '2px solid #1a1a1a',
              boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)'
            }}
          >
            <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">Nuestra filosofía</span>
          </div>
          
          {/* Title with CAPACIDAD highlighted in pink */}
          <h2 className="font-poppins font-black text-3xl md:text-4xl lg:text-5xl text-text-dark leading-tight" data-testid="promise-title">
            CONSTRUIMOS{' '}
            <span className="text-pink">CAPACIDAD</span>{' '}
            INSTALADA, NO DEPENDENCIA
          </h2>
          
          {/* Descriptive paragraph */}
          <p className="font-poppins text-lg md:text-xl text-text-dark/80 leading-relaxed">
            No creemos en soluciones externas que se caen cuando el consultor se va. 
            Diseñamos procesos, sistemas y equipos fortalecidos para que tu organización 
            siga creciendo de forma sostenible y sostenida.
          </p>

          {/* Value Pills - Neo-brutalist solid blocks */}
          <div className="flex flex-wrap gap-4 pt-2">
            {["Procesos ágiles", "Sistemas sostenibles", "Equipos autónomos"].map((item, i) => (
              <div 
                key={i} 
                className="bg-lime px-5 py-3"
                style={{ 
                  border: '2px solid #1a1a1a',
                  boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)'
                }}
              >
                <span className="font-poppins font-bold text-sm text-text-dark uppercase tracking-wide">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Visual - Clean rectangular frame, no rotation */}
        <div className="relative order-1 md:order-2">
          <div 
            className="w-full overflow-hidden"
            style={{ 
              border: '4px solid #1a1a1a',
              boxShadow: '8px 8px 0px 0px rgba(0,0,0,1)',
              borderRadius: '0px'
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_project-status-62/artifacts/ufd8rnl3_GESTIO%CC%81NA%CC%81GILDEPROYECTOSKAS58.jpg"
              alt="Equipo empoderado construyendo capacidad"
              className="w-full h-auto"
              style={{ 
                display: 'block',
                borderRadius: '0px'
              }}
              data-testid="promise-image"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Contact Section (Pink Background)
// WhatsApp Icon SVG Component
const WhatsAppIcon = ({ className = "" }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Contact Section - Simplified with WhatsApp and Calendar buttons
const ContactSection = () => {
  return (
    <section id="contacto" className="bg-pink py-20 md:py-32" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-8">
          {/* Badge */}
          <div className="inline-block bg-white border-4 border-text-dark px-8 py-4" style={{ boxShadow: '6px 6px 0px #000', borderRadius: '0px' }}>
            <span className="font-poppins font-bold text-xl text-text-dark uppercase tracking-wide">¡Contáctanos!</span>
          </div>
          
          {/* Title */}
          <h2 className="font-poppins font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight" data-testid="contact-title">
            El impacto no se hace solo
          </h2>
          
          {/* Subtitle */}
          <p className="font-poppins text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
            ¿Hablamos? Cuéntanos sobre tu organización y cómo podemos ayudarte a multiplicar tu impacto.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center pt-8">
            {/* WhatsApp Button */}
            <a
              href="https://wa.me/593982525226?text=Hola%20Boost%20Change%2C%20quiero%20conocer%20más%20sobre%20sus%20servicios."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-lime text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-5 border-4 border-text-dark hover:translate-y-1 transition-all"
              style={{ boxShadow: '6px 6px 0px #000', borderRadius: '0px' }}
              data-testid="contact-whatsapp-button"
            >
              <WhatsAppIcon className="w-6 h-6" />
              Escríbenos por WhatsApp
            </a>

            {/* Calendar Button */}
            <a
              href="https://calendar.app.google/NwvZjaatfKjuK3dp8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-yellow text-text-dark font-poppins font-bold uppercase tracking-widest px-8 py-5 border-4 border-text-dark hover:translate-y-1 transition-all"
              style={{ boxShadow: '6px 6px 0px #000', borderRadius: '0px' }}
              data-testid="contact-calendar-button"
            >
              <Calendar className="w-6 h-6" />
              Agendar una reunión
            </a>
          </div>

          {/* Email */}
          <div className="pt-6">
            <p className="font-poppins text-white/80 mb-2">O escríbenos a:</p>
            <a 
              href="mailto:hola@boost-change.com" 
              className="font-poppins font-bold text-2xl text-white underline decoration-4 decoration-yellow hover:decoration-cyan transition-colors"
            >
              hola@boost-change.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-text-dark py-12" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        {/* Logo */}
        <div>
          <span className="font-poppins font-black text-2xl text-white">
            BOOST<span className="text-pink">CHANGE</span>
          </span>
          <p className="mt-2 font-poppins text-white/60 text-sm">
            Impulsando el cambio social en America Latina
          </p>
        </div>

        {/* Links */}
        <div className="flex flex-wrap gap-6 justify-center">
          {['Inicio', 'Servicios', 'Metodologia', 'Contacto'].map((link) => (
            <a
              key={link}
              href={`#${link.toLowerCase()}`}
              className="font-poppins font-semibold text-white/80 hover:text-pink transition-colors"
            >
              {link}
            </a>
          ))}
        </div>

        {/* Social/Copyright */}
        <div className="text-right">
          <p className="font-poppins text-white/60 text-sm">
            2024 Boost Change. Ecuador.
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="landing-page">
      <Navigation />
      <main>
        <HeroSection />
        <ProblemSection />
        <AboutSection />
        <ServicesSection />
        <MethodologySection />
        <PromiseSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
